import React, { useEffect, useState } from "react";
import api from "../../../axiosConfig";
import { Button, Form, Spinner, Alert, Badge } from "react-bootstrap";
import { popup } from "../../common/popup";
import * as XLSX from "xlsx";

export default function StockHistoryAdminTab() {
  const [histories, setHistories] = useState([]);
  const [filteredHistories, setFilteredHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchHistories();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [histories, search, typeFilter, startDate, endDate]);

  /* =========================
     FETCH HISTORY ADMIN
  ========================= */
  const fetchHistories = async () => {
    try {
      setLoading(true);
      const res = await api.get("/stock-items/history-admin");
      setHistories(res.data || []);
    } catch (err) {
      console.error(err);
      setError("❌ Gagal mengambil history admin");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     HAPUS HISTORY
  ========================= */
  const deleteHistory = async (id) => {
    const ok = await popup.confirm("Yakin hapus history ini?");
    if (!ok) return;
    try {
      await api.delete(`/stock-items/history/${id}`);
      setHistories((prev) => prev.filter((h) => h.id !== id));
      popup.success("Data berhasil dihapus");
    } catch (err) {
      console.error(err);
      popup.alert("❌ Gagal hapus history");
    }
  };

  /* =========================
     FILTER
  ========================= */
  const applyFilters = () => {
    let data = [...histories];

    // 🔍 SEARCH
    if (search) {
      const lower = search.toLowerCase();
      data = data.filter(
        (h) =>
          h.part_name?.toLowerCase().includes(lower) ||
          h.kode?.toLowerCase().includes(lower) ||
          h.spesifikasi?.toLowerCase().includes(lower) ||
          h.note?.toLowerCase().includes(lower),
      );
    }

    // 🔁 IN / OUT
    if (typeFilter === "in") {
      data = data.filter((h) => h.qty_in > 0);
    } else if (typeFilter === "out") {
      data = data.filter((h) => h.qty_out > 0);
    }

    // 📅 TANGGAL
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      data = data.filter((h) => new Date(h.created_at) >= start);
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      data = data.filter((h) => new Date(h.created_at) <= end);
    }

    setFilteredHistories(data);
  };

  /* =========================
     EXPORT EXCEL
  ========================= */
  const exportToExcel = () => {
    if (filteredHistories.length === 0) {
      popup.alert("Data masih kosong");
      return;
    }

    const excelData = filteredHistories.map((h, i) => ({
      No: i + 1,
      Tanggal: new Date(h.created_at).toLocaleString("id-ID", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
      Kode: h.kode,
      "Nama Barang": h.part_name,
      Spesifikasi: h.spesifikasi || "-",
      "Stock Awal": h.stock_before,
      Selisih:
        h.qty_in > 0 ? `+${h.qty_in}` : h.qty_out > 0 ? `-${h.qty_out}` : 0,
      "Stock Aktual": h.stock_after,
      Keterangan: h.note || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "History Admin");
    XLSX.writeFile(workbook, "History_Stock_Admin.xlsx");
  };

  return (
    <div
      className="shortlife-container"
      style={{ marginTop: "-25px", height: "calc(108vh - 120px)" }}
    >
      {/* ================= HEADER ================= */}
      <div className="header-bar mb-3">
        <h2>HISTORY STOCK ADMIN</h2>
        <Button variant="success" onClick={exportToExcel}>
          Export Excel
        </Button>
      </div>

      {/* ================= FILTER ================= */}
      <div className="mb-3 d-flex justify-content-end">
        <div
          className="d-flex flex-wrap align-items-end gap-3"
          style={{ marginTop: "-20px" }}
        >
          {/* 🔍 SEARCH */}
          <Form.Group style={{ minWidth: "250px" }}>
            <Form.Control
              placeholder="🔍 Cari..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Form.Group>

          {/* 📅 DARI */}
          <Form.Group>
            <Form.Label className="mb-1">Dari tanggal</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Form.Group>

          {/* 📅 SAMPAI */}
          <Form.Group>
            <Form.Label className="mb-1">Sampai tanggal</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Form.Group>

          {/* 🔁 RESET */}
          <Button
            variant="danger"
            onClick={() => {
              setSearch("");
              setTypeFilter("all");
              setStartDate("");
              setEndDate("");
            }}
          >
            Reset
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* ================= TABLE ================= */}
      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="preview-table">
            <thead>
              <tr>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Tanggal
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Nama Barang
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Spesifikasi
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Stock Awal
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Selisih
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Stock Aktual
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  {" "}
                  Keterangan
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>Aksi</th>
              </tr>
            </thead>

            <tbody>
              {filteredHistories.map((h, i) => (
                <tr key={h.id}>
                  <td className="center">{i + 1}</td>

                  <td>{new Date(h.created_at).toLocaleString("id-ID", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}</td>

                  <td className="bold cell-left">{h.part_name}</td>

                  <td className="cell-left">{h.spesifikasi || "-"}</td>

                  {/* STOCK AWAL */}
                  <td className="center">{h.stock_before}</td>

                  {/* SELISIH */}
                  <td className="center">
                    {h.qty_in > 0 ? (
                      <Badge bg="success">+{h.qty_in}</Badge>
                    ) : h.qty_out > 0 ? (
                      <Badge bg="danger">-{h.qty_out}</Badge>
                    ) : (
                      "-"
                    )}
                  </td>

                  {/* STOCK AKTUAL */}
                  <td className="center">{h.stock_after}</td>

                  <td className="cell-left">{h.note || "-"}</td>

                  {/* AKSI */}
                  <td className="center">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => deleteHistory(h.id)}
                    >
                      Hapus
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
